import type { DocumentData } from "firebase/firestore";

import { getAccountPath } from "../workspace/collectionIds";

/** Doc id under `events/{eventId}/tasks/{taskId}`. */
export type EventTaskId =
  | "provisioning"
  | "contentPublish"
  | "levelStats"
  | "learningStats"
  | "individualRanking"
  | "teamRanking";

export type EventTaskStatus = "running" | "succeeded" | "failed";

/**
 * Current run of one background task.
 * `progress` is a percent from 0 to 100, not a step counter.
 * It is 0 when the run starts, it only increases, and it is 100 when the
 * run succeeds. The UI shows the number as-is.
 */
export type EventTask = {
  status: EventTaskStatus;
  startedAt?: Date;
  completedAt?: Date;
  cooldownUntil?: Date;
  error?: string;
  generation?: number;
  /**
   * Percent 0–100 of the current generation. Not a step counter.
   * 0 when the run is acquired, only increases, and 100 when it succeeds.
   * Each task decides what the values in between mean.
   */
  progress: number;
};

export function eventTaskPath(
  accountId: string,
  eventId: string,
  taskId: EventTaskId
): string {
  return `${getAccountPath({ accountId })}/events/${eventId}/tasks/${taskId}`;
}

function readDate(raw: unknown): Date | undefined {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw;
  }
  if (
    raw != null &&
    typeof raw === "object" &&
    "toDate" in raw &&
    typeof (raw as { toDate: () => Date }).toDate === "function"
  ) {
    const date = (raw as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

/** Reads a task snapshot. Unknown statuses are ignored. */
export function parseEventTask(raw: unknown): EventTask | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  const data = raw as DocumentData;
  const status = data.status;
  if (status !== "running" && status !== "succeeded" && status !== "failed") {
    return null;
  }
  const progressRaw = data.progress;
  const progress =
    typeof progressRaw === "number" && Number.isFinite(progressRaw)
      ? Math.min(100, Math.max(0, Math.floor(progressRaw)))
      : 0;
  return {
    status,
    startedAt: readDate(data.startedAt),
    completedAt: readDate(data.completedAt),
    cooldownUntil: readDate(data.cooldownUntil),
    error: typeof data.error === "string" ? data.error : undefined,
    generation:
      typeof data.generation === "number" ? data.generation : undefined,
    progress,
  };
}
